import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import NotFound from '@/pages/NotFound';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    if (article) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [article]);

  if (!article) {
    return <NotFound />;
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://www.oumeijingpin.com/articles/${article.slug}`,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={`${article.title}｜欧美精品`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.oumeijingpin.com/articles/${article.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '品牌资讯', href: '/articles' },
            { label: article.title },
          ]}
        />

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[11px]">
                {article.category}
              </span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-1 mb-6">
              {article.tags.map((tag) => (
                <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 欧美精品品牌资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <Link
              to="/articles"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}